import { liquidToJSONata } from '@norkin/autocast';
import { resolveEdgeSchemas } from '@features/flows/lib/autocast/edge-context';
import { synthesizeMapping } from '@features/flows/lib/autocast/llm';
import { flattenProperties } from '@features/flows/lib/autocast/schema-utils';
import type { CanvasState, SliceCreator } from '../types';
import type { SliceCtx } from '../ctx';

const compileFromLiquid = (liquid?: Record<string, string>): { jsonata: string } => {
  if (!liquid || Object.keys(liquid).length === 0) return { jsonata: '' };
  try {
    const res = liquidToJSONata(liquid);
    return { jsonata: typeof res?.jsonata === 'string' ? res.jsonata : '' };
  } catch {
    return { jsonata: '' };
  }
};

type SliceKeys =
  | 'afterEdgeConnected'
  | 'clearEdgeMapping'
  | 'setEdgeLiquid'
  | 'setEdgeJsonata'
  | 'setJsonataOverride'
  | 'setEdgeInstructions'
  | 'llmAutocastEdge';

export const createMappingSlice: SliceCreator<Pick<CanvasState, SliceKeys>> = (set, get) => {
  const resolveCtx = (): SliceCtx | undefined => (get as any).__ctx;
  const ensureCtx = () => {
    const ctx = resolveCtx();
    if (!ctx) throw new Error('canvas ctx missing for mapping slice');
    return ctx;
  };

  return {
    afterEdgeConnected: (edgeId) => {
      const { debugLog } = ensureCtx();
      const state = get();
      const edge = state.graph.edges.find((e) => e.id === edgeId);
      if (!edge) return;
      const data = (edge.data as Record<string, unknown>) ?? {};

      const channel = typeof data.channel === 'string' ? data.channel : 'data';
      if (channel !== 'data') return;

      const sourceCoarse =
        typeof data.sourceCoarse === 'string' ? data.sourceCoarse.toLowerCase() : undefined;
      const targetCoarse =
        typeof data.targetCoarse === 'string' ? data.targetCoarse.toLowerCase() : undefined;

      const coarseMismatch =
        !!sourceCoarse &&
        !!targetCoarse &&
        sourceCoarse !== 'any' &&
        targetCoarse !== 'any' &&
        sourceCoarse !== targetCoarse;

      if (!coarseMismatch) return;

      const mappingLiquid = (data.mappingLiquid as Record<string, string> | undefined) ?? undefined;
      const hasLiquid = Boolean(mappingLiquid && Object.keys(mappingLiquid).length > 0);
      const hasJsonata =
        typeof data.mappingJSONata === 'string' && data.mappingJSONata.trim().length > 0;
      const hasOverride = data.mappingJsonataOverride === true;
      if (hasLiquid || hasJsonata || hasOverride) return;

      debugLog('afterEdgeConnected:autocast', {
        edgeId,
        sourceCoarse,
        targetCoarse,
      });
      const autocast = state.llmAutocastEdge;
      if (typeof autocast === 'function') autocast(edgeId);
    },

    clearEdgeMapping: (edgeId) => {
      ensureCtx();
      set((state) => {
        const edge = state.graph.edges.find((e) => e.id === edgeId);
        if (!edge) return;
        const data = { ...((edge.data as Record<string, unknown>) ?? {}) };
        delete (data as any).mappingLiquid;
        delete (data as any).mappingLiquidVersion;
        delete (data as any).mappingJSONata;
        delete (data as any).mappingJsonataOverride;
        delete (data as any).mappingError;
        delete (data as any).mappingLLMRunning;
        delete (data as any).mappingLLMError;
        delete (data as any).mappingSynthesis;
        delete (data as any).mappingInstructions;
        edge.data = data;
      });
    },

    setEdgeLiquid: (edgeId, to, code) => {
      const { debugLog } = ensureCtx();
      debugLog('setEdgeLiquid', { edgeId, to });
      set((state) => {
        const edge = state.graph.edges.find((e) => e.id === edgeId);
        if (!edge) return;
        const data = { ...((edge.data as Record<string, unknown>) ?? {}) } as Record<string, any>;
        const current = (data.mappingLiquid as Record<string, string> | undefined) ?? {};
        const next = { ...current };
        if (code == null) {
          delete next[to];
        } else {
          next[to] = String(code);
        }
        if (Object.keys(next).length > 0) {
          data.mappingLiquid = next;
          data.mappingLiquidVersion = Date.now();
        } else {
          delete data.mappingLiquid;
          delete data.mappingLiquidVersion;
        }
        if (!data.mappingJsonataOverride) {
          const { jsonata } = compileFromLiquid(data.mappingLiquid as Record<string, string> | undefined);
          data.mappingJSONata = jsonata;
        }
        edge.data = data;
      });
    },

    setEdgeJsonata: (edgeId, jsonata) => {
      const { debugLog } = ensureCtx();
      debugLog('setEdgeJsonata', { edgeId });
      set((state) => {
        const edge = state.graph.edges.find((e) => e.id === edgeId);
        if (!edge) return;
        const data = { ...((edge.data as Record<string, unknown>) ?? {}) } as Record<string, any>;
        data.mappingJSONata = String(jsonata ?? '');
        data.mappingJsonataOverride = true;
        edge.data = data;
      });
    },

    setJsonataOverride: (edgeId, enabled) => {
      const { debugLog } = ensureCtx();
      debugLog('setJsonataOverride', { edgeId, enabled });
      set((state) => {
        const edge = state.graph.edges.find((e) => e.id === edgeId);
        if (!edge) return;
        const data = { ...((edge.data as Record<string, unknown>) ?? {}) } as Record<string, any>;
        data.mappingJsonataOverride = enabled;
        if (!enabled) {
          const { jsonata } = compileFromLiquid(data.mappingLiquid as Record<string, string> | undefined);
          data.mappingJSONata = jsonata;
        }
        edge.data = data;
      });
    },

    setEdgeInstructions: (edgeId, instructions) => {
      const { debugLog } = ensureCtx();
      debugLog('setEdgeInstructions', { edgeId });
      set((state) => {
        const edge = state.graph.edges.find((e) => e.id === edgeId);
        if (!edge) return;
        const data = { ...((edge.data as Record<string, unknown>) ?? {}) } as Record<string, any>;
        data.mappingInstructions = String(instructions ?? '');
        edge.data = data;
      });
    },

    llmAutocastEdge: (edgeId, explicitUnresolved) => {
      const { debugLog, inflightLLM } = ensureCtx();
      debugLog('llmAutocastEdge:start', { edgeId, explicitUnresolved });
      const state = get();
      const edge = state.graph.edges.find((e) => e.id === edgeId);
      if (!edge) return;
      if (inflightLLM.has(edgeId)) return;
      inflightLLM.add(edgeId);

      const context = resolveEdgeSchemas(edge as any, state.graph.nodes, state.templates);
      const initialData = ((edge.data as Record<string, unknown>) ?? {}) as Record<string, any>;
      const currentLiquid = (initialData.mappingLiquid as Record<string, string> | undefined) ?? {};
      const instructions = typeof initialData.mappingInstructions === 'string' ? initialData.mappingInstructions : undefined;
      const targetRoot = context.targetInputPath ?? context.rawTarget ?? null;

      const { unresolvedTargets, resolvedTargets } = (() => {
        // If explicit unresolved targets are provided, use them directly
        if (explicitUnresolved && Array.isArray(explicitUnresolved)) {
          console.log('[Mapping] ========== RESOLVING TARGETS ==========');
          console.log('[Mapping] explicitUnresolved received:', explicitUnresolved);
          console.log('[Mapping] currentLiquid keys:', Object.keys(currentLiquid));
          console.log('[Mapping] currentLiquid:', JSON.stringify(currentLiquid, null, 2));

          // Unresolved: only user-added fields that don't have a valid liquid expression
          const unresolved: string[] = [];
          for (const target of explicitUnresolved) {
            const trimmed = String(target).trim();
            console.log(`[Mapping] Processing explicit target: "${trimmed}"`);
            if (!trimmed) {
              console.log('[Mapping]   -> Skipped (empty)');
              continue;
            }
            const existingValue = currentLiquid[trimmed];
            console.log(`[Mapping]   -> existingValue:`, JSON.stringify(existingValue));
            if (typeof existingValue !== 'string' || existingValue.trim().length === 0) {
              unresolved.push(trimmed);
              console.log(`[Mapping]   -> UNRESOLVED (no value or empty)`);
            } else {
              console.log(`[Mapping]   -> Has value, skipping (already mapped)`);
            }
          }

          // Resolved: ALL fields in currentLiquid that have non-empty values
          const resolved: string[] = [];
          for (const [field, value] of Object.entries(currentLiquid)) {
            const trimmedField = field.trim();
            const trimmedValue = typeof value === 'string' ? value.trim() : '';
            if (trimmedField && trimmedValue.length > 0) {
              resolved.push(trimmedField);
              console.log(`[Mapping] Field "${trimmedField}" -> RESOLVED (has value)`);
            }
          }

          console.log('[Mapping] ========== RESULTS ==========');
          console.log('[Mapping] Final unresolved targets:', unresolved);
          console.log('[Mapping] Final resolved targets:', resolved);
          console.log('[Mapping] Current liquid mappings being sent:', currentLiquid);
          console.log('[Mapping] ===================================');
          return {
            unresolvedTargets: unresolved.sort((a, b) => a.localeCompare(b)),
            resolvedTargets: resolved.sort((a, b) => a.localeCompare(b)),
          };
        }
        console.log('[Mapping] No explicit unresolved, computing from schema');

        // Otherwise, compute from schema (original behavior for automatic autocast)
        const stripTargetPrefix = (path: string): string => {
          if (!targetRoot) return path;
          const trimmed = path.trim();
          if (!trimmed) return trimmed;
          if (trimmed === targetRoot) return '';
          if (trimmed.startsWith(`${targetRoot}.`)) return trimmed.slice(targetRoot.length + 1);
          if (trimmed.startsWith(`${targetRoot}[`)) return trimmed.slice(targetRoot.length);
          return trimmed;
        };

        const rawMapped = new Set<string>();
        const normalizedMapped = new Set<string>();
        for (const [path, value] of Object.entries(currentLiquid)) {
          if (typeof value !== 'string' || value.trim().length === 0) continue;
          const trimmed = path.trim();
          if (!trimmed) continue;
          rawMapped.add(trimmed);
          const normalized = stripTargetPrefix(trimmed);
          if (normalized && normalized !== trimmed) {
            normalizedMapped.add(normalized);
          } else {
            normalizedMapped.add(trimmed);
          }
        }

        const candidateIsMapped = (candidate: string): boolean => {
          if (!candidate) return rawMapped.has(targetRoot ?? '');
          if (rawMapped.has(candidate) || normalizedMapped.has(candidate)) return true;
          if (targetRoot) {
            const withRoot =
              candidate.startsWith('[') || candidate.startsWith('.')
                ? `${targetRoot}${candidate}`
                : `${targetRoot}.${candidate}`;
            if (rawMapped.has(withRoot)) return true;
          }
          return false;
        };

        const unresolved = new Set<string>();

        const schemaCandidates: Array<{ schema: unknown; relative: boolean }> = [];
        if (context.targetSchema && typeof context.targetSchema === 'object') {
          schemaCandidates.push({ schema: context.targetSchema, relative: true });
        } else if (context.targetInputSchema && typeof context.targetInputSchema === 'object') {
          schemaCandidates.push({ schema: context.targetInputSchema, relative: false });
        }

        const pathBelongsToTarget = (path: string, relative: boolean): boolean => {
          if (relative) return true;
          if (!targetRoot) return true;
          const trimmed = path.trim();
          if (!trimmed) return true;
          if (trimmed === targetRoot) return true;
          if (trimmed.startsWith(`${targetRoot}.`)) return true;
          if (trimmed.startsWith(`${targetRoot}[`)) return true;
          return false;
        };

        for (const candidate of schemaCandidates) {
          try {
            const fields = flattenProperties(candidate.schema, { maxFields: 512 });
            for (const field of fields) {
              const rawPath = typeof field?.path === 'string' ? field.path.trim() : '';
              if (!rawPath) continue;
              if (!pathBelongsToTarget(rawPath, candidate.relative)) continue;
              const normalized = candidate.relative ? rawPath : stripTargetPrefix(rawPath);
              if (!normalized) continue;
              if (candidateIsMapped(normalized)) continue;
              unresolved.add(normalized);
            }
          } catch {
            /* ignore schema flatten errors */
          }
        }

        if (
          unresolved.size === 0 &&
          targetRoot &&
          !candidateIsMapped('') &&
          !candidateIsMapped(targetRoot)
        ) {
          const fallback =
            targetRoot.startsWith('[') || targetRoot.startsWith('.')
              ? targetRoot
              : targetRoot.includes('.')
              ? stripTargetPrefix(targetRoot)
              : '';
          if (fallback) unresolved.add(fallback);
        }

        return {
          unresolvedTargets: Array.from(unresolved).sort((a, b) => a.localeCompare(b)),
          resolvedTargets: undefined,
        };
      })();

      set((draft) => {
        const target = draft.graph.edges.find((e) => e.id === edgeId);
        if (!target) return;
        const data = { ...((target.data as Record<string, unknown>) ?? {}) } as Record<string, any>;
        data.mappingLLMRunning = true;
        data.mappingLLMError = undefined;
        target.data = data;
      });

      (async () => {
        try {
          const { mappingLiquid, jsonata, info } = await synthesizeMapping({
            srcSchema: context.sourceSchema,
            dstSchema: context.targetSchema,
            unresolved: unresolvedTargets,
            resolved: resolvedTargets,
            currentLiquid: currentLiquid,
            message: instructions,
          });

          set((draft) => {
            const target = draft.graph.edges.find((e) => e.id === edgeId);
            if (!target) return;
            const data = { ...((target.data as Record<string, unknown>) ?? {}) } as Record<string, any>;

            if (mappingLiquid && Object.keys(mappingLiquid).length > 0) {
              data.mappingLiquid = { ...mappingLiquid };
              data.mappingLiquidVersion = Date.now();
            }

            const sourceLiquids = data.mappingLiquid as Record<string, string> | undefined;
            const resolvedJsonata = (() => {
              if (typeof jsonata === 'string' && jsonata.trim().length > 0) return jsonata;
              return compileFromLiquid(sourceLiquids).jsonata;
            })();

            data.mappingJSONata = resolvedJsonata;
            data.mappingJsonataOverride = false;
            data.mappingLLMRunning = false;
            data.mappingLLMError = undefined;
            if (info) data.mappingSynthesis = info;
            else delete data.mappingSynthesis;
            target.data = data;
          });
          debugLog('llmAutocastEdge:finished', { edgeId });
        } catch (err) {
          console.warn('llmAutocastEdge failed', err);
          set((draft) => {
            const target = draft.graph.edges.find((e) => e.id === edgeId);
            if (!target) return;
            const data = { ...((target.data as Record<string, unknown>) ?? {}) } as Record<string, any>;
            data.mappingLLMError = err instanceof Error ? err.message : String(err ?? 'LLM synthesis failed');
            target.data = data;
          });
        } finally {
          inflightLLM.delete(edgeId);
          set((draft) => {
            const target = draft.graph.edges.find((e) => e.id === edgeId);
            if (!target) return;
            const data = { ...((target.data as Record<string, unknown>) ?? {}) } as Record<string, any>;
            data.mappingLLMRunning = false;
            target.data = data;
          });
        }
      })();
    },
  };
};
